<template>
  <div class="order-plan" v-loading="loading">
    <!-- 顶部操作区 -->
    <div class="plan-toolbar">
      <!-- i18n：创建方案、生成默认方案、视图切换文案统一来自 message -->
      <div class="plan-toolbar-left">
        <span class="plan-count">{{ planCount }}/{{ planMax }}</span>
        <el-button class="danger-button" size="mini" @click="openCreatePlan">{{ $t('message.createPlan') }}</el-button>
      </div>
      <div class="plan-toolbar-space"></div>
      <el-button class="danger-button" size="mini" @click="generateDefaultPlan">
        {{ $t('message.generateDefaultPlan') }}
      </el-button>
      <el-button-group class="view-button">
        <el-button size="mini" icon="el-icon-s-fold" :class="{ active: viewType == 'list' }"
          :title="$t('message.listView')" @click="changeViewType('list')"></el-button>
        <el-button size="mini" icon="el-icon-s-grid" :class="{ active: viewType == 'card' }"
          :title="$t('message.cardView')" @click="changeViewType('card')"></el-button>
      </el-button-group>
    </div>

    <!-- 下单方案卡片 -->
    <div class="plan-list" v-if="planList.length && viewType == 'card'">
      <div class="plan-item" v-for="plan in planList" :key="plan.id">
        <!-- 方案标题和操作 -->
        <div class="plan-head">
          <div class="plan-name">
            <el-checkbox :value="selectedPlanIds.indexOf(plan.id) !== -1"
              @change="selectPlan(plan.id, $event)"></el-checkbox>
            <span :title="plan.name">{{ plan.name }}</span>
          </div>
          <!-- i18n：默认下单、删除、编辑按钮文案统一来自 message -->
          <div class="plan-action">
            <el-checkbox :value="plan.is_default == 1" :disabled="settingDefaultId !== null"
              @change="setDefaultPlan(plan, $event)">{{ $t('message.defaultOrder') }}</el-checkbox>
            <el-button class="danger-button" size="mini" @click="deletePlan(plan)">{{ $t('message.delete')
            }}</el-button>
            <el-button class="success-button" size="mini" @click="openEditPlan(plan)">{{ $t('message.edit')
              }}</el-button>
          </div>
        </div>

        <!-- 主商品信息 -->
        <div class="plan-section">
          <!-- i18n：卡片分区标题和字段名使用 message，商品数据直接使用接口返回值 -->
          <div class="section-name">{{ $t('message.mainProductInfo') }}</div>
          <div class="main-product">
            <div class="main-product-info">
              <div class="sub-name">{{ $t('message.mainProductInfo') }}</div>
              <div class="product-info-content">
                <div class="product-image">
                  <el-image v-if="plan.pic" :src="plan.pic" fit="cover" :preview-src-list="[plan.pic]" />
                </div>
                <div class="sku-list">
                  <p v-for="(sku, skuIndex) in plan.detail" :key="skuIndex">
                    <span>{{ sku.key }}</span>{{ sku.value }}
                  </p>
                </div>
              </div>
            </div>
            <div class="main-product-column additional-service-column" :title="getServiceTitle(plan.option)">
              <div class="sub-name">{{ $t('message.additionalServices') }}</div>
              <div class="service-list">
                <p v-for="(service, serviceIndex) in plan.option" :key="serviceIndex">
                  {{ service.name }}*{{ service.num }}
                </p>
              </div>
            </div>
            <div class="main-product-column main-product-remark">
              <div class="sub-name">{{ $t('message.remark') }}</div>
              <p :title="plan.client_remark">{{ plan.client_remark }}</p>
            </div>
          </div>
        </div>

        <!-- 组套商品信息 -->
        <div class="plan-section" v-if="plan.client_product_order_plan_child.length">
          <!-- i18n：组套商品分区标题使用 message.comboProductInfo -->
          <div class="section-name">{{ $t('message.comboProductInfo') }}</div>
          <div class="small-card-list">
            <div class="small-card combo-card" v-for="product in plan.client_product_order_plan_child"
              :key="product.id">
              <div class="product-image small">
                <el-image v-if="product.pic" :src="product.pic" fit="cover" :preview-src-list="[product.pic]" />
              </div>
              <div class="small-card-content">
                <p class="small-card-name combo-name" :title="product.name">
                  <i class="el-icon-link"></i>
                  <span>{{ product.name }}</span>
                </p>
                <p v-for="(sku, skuIndex) in product.detail" :key="skuIndex">
                  <span>{{ sku.key }}</span>{{ sku.value }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- 辅料绑定 -->
        <div class="plan-section" v-if="plan.material.length">
          <!-- i18n：辅料标题、类型、消耗单位来自 message -->
          <div class="section-name">{{ $t('message.materialBinding') }}</div>
          <div class="small-card-list">
            <div class="small-card" v-for="material in plan.material" :key="material.material_id">
              <div class="product-image small">
                <el-image v-if="material.material_detail.main_image" :src="material.material_detail.main_image"
                  fit="cover" :preview-src-list="[material.material_detail.main_image]" />
              </div>
              <div class="small-card-content">
                <p class="small-card-name" :title="material.material_detail.name_trans">{{ material.material_detail.name_trans }}
                </p>
                <p><span>{{ $t('message.productType') }}</span>{{ material.material_detail.type.name }}</p>
                <p><span>{{ $t('message.materialConsumption') }}</span><b>{{ material.consume }}</b>{{
                  $t('message.pointPerSet') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 标签信息 -->
        <div class="plan-section" v-if="plan.order_detail_tag.length">
          <!-- i18n：标签字段名来自 message，标签类型名称来自接口 type_translate -->
          <div class="section-name">{{ $t('message.tagInformation') }}</div>
          <div class="tag-item" v-for="(tag, tagIndex) in plan.order_detail_tag" :key="tagIndex">
            <div class="sub-name">{{ tag.type_translate }}</div>
            <div class="tag-content">
              <div class="tag-base">
                <p><span>{{ $t('message.labelNumber') }}</span>{{ tag.no }}</p>
                <p><span>{{ $t('message.commodityManagementCode') }}</span>{{ tag.goods_no }}</p>
                <p><span>{{ $t('message.encodingFormat') }}</span>{{ tag.code_type }}</p>
              </div>
              <div class="tag-text">
                <p><span>{{ $t('message.labelText') }}(1)</span>{{ tag.text_line_one }}</p>
                <p><span>{{ $t('message.labelText') }}(2)</span>{{ tag.text_line_two }}</p>
                <p><span>{{ $t('message.labelText') }}(3)</span>{{ tag.text_line_three }}</p>
                <p><span>{{ $t('message.labelText') }}(4)</span>{{ tag.text_line_four }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 商品文件 -->
        <div class="plan-section" v-if="plan.files.length">
          <!-- i18n：商品文件分区标题来自 message.productFile -->
          <div class="section-name">{{ $t('message.productFile') }}</div>
          <div class="file-list">
            <a v-for="(file, fileIndex) in plan.files" :key="file.url" :href="file.url" target="_blank"
              rel="noopener noreferrer" class="a-link">{{ fileIndex + 1 }}.{{ file.name }}</a>
          </div>
        </div>

        <!-- 方案最新更新时间 -->
        <div class="update-time" v-if="plan.updated_at">
          {{ $t('message.planLatestUpdateTime') }}：{{ plan.updated_at }}
        </div>
      </div>
    </div>

    <!-- 下单方案列表 -->
    <div class="plan-table" v-else-if="planList.length">
      <div class="plan-table-scroll">
        <!-- i18n：列表列头统一使用 message；SHKU 是固定业务列名，不翻译 -->
        <div class="plan-table-head plan-table-grid">
          <div>{{ $t('message.choose') }}</div>
          <div>{{ $t('message.defaultOrder') }}</div>
          <div>{{ $t('message.planName') }}</div>
          <div>{{ $t('message.picture') }}</div>
          <div>SHKU</div>
          <div>{{ $t('message.quantity') }}</div>
          <div>{{ $t('message.additionalServices') }}</div>
          <div>{{ $t('message.tagInformation') }}</div>
          <div>{{ $t('message.commodityNote') }}</div>
          <div>{{ $t('message.materialBinding') }}</div>
          <div>{{ $t('message.operation') }}</div>
        </div>

        <div class="plan-table-item" v-for="plan in planList" :key="'list-' + plan.id">
          <!-- 主商品列表行：列顺序需与表头保持一致 -->
          <div class="plan-table-row plan-table-grid">
            <div class="selection-cell">
              <el-checkbox :value="selectedPlanIds.indexOf(plan.id) !== -1"
                @change="selectPlan(plan.id, $event)"></el-checkbox>
            </div>
            <div class="default-order-cell">
              <el-checkbox :value="plan.is_default == 1" :disabled="settingDefaultId !== null"
                @change="setDefaultPlan(plan, $event)">{{ $t('message.defaultOrder') }}</el-checkbox>
            </div>
            <div class="plan-name-cell" :title="plan.name">{{ plan.name }}</div>
            <div>
              <div class="product-image list-image">
                <el-image v-if="plan.pic" :src="plan.pic" fit="cover" :preview-src-list="[plan.pic]" />
              </div>
            </div>
            <div class="cell-lines">
              <p v-for="(sku, skuIndex) in plan.detail" :key="skuIndex" :title="sku.key + sku.value">
                <span>{{ sku.key }}</span>{{ sku.value }}
              </p>
            </div>
            <div class="num-cell">{{ plan.num }}</div>
            <div class="cell-lines service-cell" :title="getServiceTitle(plan.option)">
              <div class="service-list">
                <p v-for="(service, serviceIndex) in plan.option" :key="serviceIndex">
                  {{ service.name }}*{{ service.num }}
                </p>
              </div>
            </div>
            <div class="tag-list-cell">
              <div class="tag-mini" v-for="(tag, tagIndex) in plan.order_detail_tag" :key="tagIndex">
                <p>{{ tag.type_translate }}</p>
                <p>{{ tag.no }}</p>
                <p>{{ tag.goods_no }}</p>
              </div>
            </div>
            <div class="cell-lines remark-cell" :title="plan.client_remark">
              <p>{{ plan.client_remark }}</p>
            </div>
            <div class="cell-lines">
              <p v-for="material in plan.material" :key="material.material_id" :title="material.material_detail.name_trans">
                {{ material.material_detail.name_trans }}*{{ material.consume }}
              </p>
            </div>
            <div class="list-actions">
              <el-button type="text" @click="openEditPlan(plan)">{{ $t('message.edit') }}</el-button>
              <el-button type="text" class="delete-link" @click="deletePlan(plan)">{{ $t('message.delete')
                }}</el-button>
            </div>
          </div>

          <!-- i18n：组套商品展开入口使用 message.comboProduct -->
          <div class="combo-toggle" v-if="plan.client_product_order_plan_child.length" @click="toggleCombo(plan)">
            <i :class="isComboExpanded(plan) ? 'el-icon-caret-bottom' : 'el-icon-caret-right'"></i>
            {{ $t('message.comboProduct') }}
          </div>
          <div class="combo-list" v-if="plan.client_product_order_plan_child.length && isComboExpanded(plan)">
            <!-- 组套商品列表行：沿用主商品列宽，空列用于对齐表头 -->
            <div class="combo-list-row plan-table-grid"
              v-for="(product, productIndex) in plan.client_product_order_plan_child"
              :key="'child-list-' + plan.id + '-' + productIndex">
              <div></div>
              <div></div>
              <div class="plan-name-cell" :title="product.name">{{ product.name || $t('message.comboItem') +
                (productIndex + 1) }}</div>
              <div>
                <div class="product-image list-image">
                  <el-image v-if="product.pic" :src="product.pic" fit="cover" :preview-src-list="[product.pic]" />
                </div>
              </div>
              <div class="cell-lines">
                <p v-for="(sku, skuIndex) in product.detail" :key="skuIndex" :title="sku.key + sku.value">
                  <span>{{ sku.key }}</span>{{ sku.value }}
                </p>
              </div>
              <div class="num-cell">{{ product.num }}</div>
              <div class="cell-lines service-cell" :title="getServiceTitle(product.option)">
                <div class="service-list">
                  <p v-for="(service, serviceIndex) in product.option" :key="serviceIndex">
                    {{ service.name }}*{{ service.num }}
                  </p>
                </div>
              </div>
              <div></div>
              <div class="cell-lines remark-cell" :title="product.client_remark">
                <p>{{ product.client_remark }}</p>
              </div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- i18n：空状态文案来自 message.noOrderPlan -->
    <div class="empty" v-else-if="!loading">{{ $t('message.noOrderPlan') }}</div>
    <!-- 创建方案弹框：保存成功后刷新当前列表 -->
    <create-plan-dialog ref="createPlanDialog" @success="orderPlanList" />
  </div>
</template>

<script>
/**
 * 下单方案列表页
 * - 展示当前商品已创建的下单方案
 * - 新增或编辑方案；保存成功后刷新列表
 */
import CreatePlanDialog from './createPlanDialog'

const VIEW_TYPE_STORAGE_KEY = 'orderPlanViewType'

export default {
  name: 'OrderPlan',
  components: {
    CreatePlanDialog
  },
  props: {
    // 父页商品详情（切换 tab 后通过 setData 注入）
    detail: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      currentDetail: {}, // 当前商品详情缓存
      planList: [], // 下单方案列表
      selectedPlanIds: [], // 当前勾选的方案 ID
      planCount: 0, // 已创建方案数
      planMax: 0, // 方案数量上限
      loading: false, // 列表加载状态
      viewType: 'card', // 展示模式：card 卡片 / list 列表
      settingDefaultId: null,
      comboExpandedMap: {} // 列表视图组套商品展开状态
    }
  },
  created() {
    this.restoreViewType()
  },
  methods: {
    // 读取浏览器保存的展示模式，仅接受列表和卡片两种有效值。
    restoreViewType() {
      const savedViewType = localStorage.getItem(VIEW_TYPE_STORAGE_KEY)
      if (['list', 'card'].includes(savedViewType)) this.viewType = savedViewType
    },
    // 切换展示模式并保存到浏览器，下次进入下单方案时继续使用。
    changeViewType(viewType) {
      this.viewType = viewType
      localStorage.setItem(VIEW_TYPE_STORAGE_KEY, viewType)
    },
    /**
     * 父页面切换到下单方案 tab 时调用
     * @param {Object} data 商品详情，需含 user_id、sys_code、client_sku_code
     */
    setData(data) {
      this.currentDetail = data
      this.orderPlanList()
    },
    // 打开创建方案弹框，并传入当前商品上下文
    openCreatePlan() {
      this.$refs.createPlanDialog.open(this.currentDetail)
    },
    // 打开编辑弹框并填充当前方案。
    openEditPlan(plan) {
      this.$refs.createPlanDialog.edit(this.currentDetail, plan)
    },
    // 根据当前管理番号最近一次下单数据生成客户默认方案。
    generateDefaultPlan() {
      this.$api.productManagementApi.generateDefaultPlan({
        user_id: this.currentDetail.user_id,
        sys_code: this.currentDetail.sys_code
      }).then(res => {
        this.$message.success(res.msg || this.$t('message.saveSuccess'))
        this.orderPlanList()
      })
    },
    // 二次确认后删除当前方案。
    deletePlan(plan) {
      this.$confirm(this.$t('message.confirmDeletePlan'), this.$t('message.prompt'), {
        confirmButtonText: this.$t('message.confirm'),
        cancelButtonText: this.$t('message.cancel'),
        type: 'warning',
        center: true
      }).then(() => {
        this.$api.productManagementApi.orderPlanDelete({
          user_id: this.currentDetail.user_id,
          sys_code: this.currentDetail.sys_code,
          id: plan.id
        }).then(res => {
          this.$message.success(res.msg)
          this.orderPlanList()
        })
      }).catch(() => { })
    },
    // 勾选或取消当前方案。
    selectPlan(id, checked) {
      const index = this.selectedPlanIds.indexOf(id)
      if (checked && index === -1) this.selectedPlanIds.push(id)
      if (!checked && index !== -1) this.selectedPlanIds.splice(index, 1)
    },
    getServiceTitle(services) {
      return services.map(item => `${item.name}*${item.num}`).join('\n')
    },
    // 勾选状态变化后立即保存，不重新请求列表。
    setDefaultPlan(plan, checked) {
      if (this.settingDefaultId !== null) return

      this.settingDefaultId = plan.id
      this.$api.productManagementApi.orderPlanSetDefault({
        user_id: this.currentDetail.user_id,
        sys_code: this.currentDetail.sys_code,
        id: plan.id,
        is_default: checked ? 1 : 0
      }).then(res => {
        if (checked) {
          this.planList.forEach(item => {
            item.is_default = item.id === plan.id ? 1 : 0
          })
        } else {
          plan.is_default = 0
        }
        this.$message.success(res.msg || this.$t('message.saveSuccess'))
      }).finally(() => {
        this.settingDefaultId = null
      })
    },
    isComboExpanded(plan) {
      return this.comboExpandedMap[plan.id] === true
    },
    toggleCombo(plan) {
      this.$set(this.comboExpandedMap, plan.id, !this.isComboExpanded(plan))
    },
    /**
     * 查询下单方案列表
     * client_sku_ode 为接口字段名
     */
    orderPlanList() {
      let datas = {
        user_id: this.currentDetail.user_id,
        sys_code: this.currentDetail.sys_code,
        client_sku_ode: this.currentDetail.client_sku_code
      }

      this.loading = true
      this.$api.productManagementApi.orderPlanList(datas).then(res => {
        this.selectedPlanIds = []
        this.planList = res.data.list
        this.planCount = res.data.plan_count
        this.planMax = res.data.plan_max
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped src="./index.scss"></style>
